import React, { useState, useRef } from "react"
import _ from "lodash"
import { Grid, Tabs, Tab, Switch, Slider, Box } from "@material-ui/core"
import { WarningOutlined as WarningIcon } from "@material-ui/icons"
import { Text } from "letalkui"

import { ChatBotFlowExtraData, ChatBotQueueCode } from "@/protocols/chatBot"
import { DeepPartial } from "@/protocols/utility"

import ApiService from "@/services/Api"
import { ActionDialog, Notification, Loading } from "@/components"

import useStyles from "@/pages/Admin/ChatBot/ChatBotList/ChatBotConfig/styles"
import {
	QUEUE_GROUPS,
	isQueueActive,
	buildCompleteAllowedQueuesMap,
	QueueGroup
} from "@/pages/Admin/ChatBot/ChatBotList/ChatBotConfig/queuesConstants"
import QueuesSection from "@/pages/Admin/ChatBot/ChatBotList/ChatBotConfig/QueuesSection"

import { useGlobalStateStore } from "@/store/GlobalState"
import useDidMount from "@/hooks/useDidMount"
import { ErrorType } from "@/hooks/useValidation"
import ErrorHandlerService from "@/services/ErrorHandler"
import ChatBotConfigSkeleton from "@/skeletons/Admin/ChatBotConfigSkeleton"

type ChatBotConfigProps = {
	chatBotFlowId: number
	title: string
	openDialog: boolean
	onClose: () => void
}

type ConfigTabType = "general" | "queues"

type ConfigTab = {
	title: string
	type: ConfigTabType
}

const MIN_DELAY = 2
const MAX_DELAY = 40
const DEFAULT_DELAY = 12

const ChatBotConfig: React.FC<ChatBotConfigProps> = (props) => {
	const {
		chatBotFlowId,
		openDialog,
		onClose
	} = props

	const [extraData, setExtraData] = useState<ChatBotFlowExtraData>({
		delay: {
			active: false,
			min_delay_in_seconds: DEFAULT_DELAY
		},
		shouldPresenceUpdateBeSentOnSendingMessages: false,
		auto_send_active: false
	})
	const [loading, setLoading] = useState<boolean>(true)
	const [sliderValue, setSliderValue] = useState<number>(DEFAULT_DELAY)

	const globalStateStore = useGlobalStateStore()
	const isWabaChannel = globalStateStore.currentChannel?.channelType === "waba"

	const configTabs: ConfigTab[] = isWabaChannel
		? [{ title: "Atendimentos e automação", type: "queues" }]
		: [
			{ title: "Geral", type: "general" },
			{ title: "Atendimentos e automação", type: "queues" }
		]

	const [activeTab, setActiveTab] = useState<ConfigTabType>(configTabs[0].type)

	const classes = useStyles()

	const putChainRef = useRef<Promise<void>>(Promise.resolve())

	const persistDelta = (delta: DeepPartial<ChatBotFlowExtraData>, revertState: ChatBotFlowExtraData) => {
		putChainRef.current = putChainRef.current.then(async () => {
			try {
				await ApiService.put(`/chat-bot/flow/${chatBotFlowId}/extra-data`, {
					extraData: delta
				})
				Notification.success({ message: "Configuração salva com sucesso." })
			} catch {
				setExtraData(revertState)
				Notification.error({ message: "Não foi possível salvar a alteração. Tente novamente." })
			}
		})
	}

	const handleLiveChange = (delta: DeepPartial<ChatBotFlowExtraData>) => {
		setExtraData(prevState => {
			const newState = _.merge({}, prevState, delta) as ChatBotFlowExtraData
			persistDelta(delta, prevState)
			return newState
		})
	}

	const handleClose = () => {
		onClose()
	}

	const handleLoadConfig = async () => {
		try {
			const result = await ApiService.get(`/chat-bot/flows/${chatBotFlowId}/config`)
			const chatBotFlowExtraData = result.data.config as ChatBotFlowExtraData
			setExtraData(prevState => {
				const merged = _.merge({}, prevState, chatBotFlowExtraData) as ChatBotFlowExtraData
				return merged
			})
			setSliderValue(chatBotFlowExtraData?.delay?.min_delay_in_seconds || DEFAULT_DELAY)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useDidMount(async () => {
		setLoading(true)
		await handleLoadConfig()
		setLoading(false)
	})

	const queueGroups = QUEUE_GROUPS
	const allowedQueues = extraData.allowedQueues

	const handleToggleQueue = (code: ChatBotQueueCode) => {
		setExtraData(prevState => {
			const overrides: Partial<Record<ChatBotQueueCode, boolean>> = {
				[code]: !isQueueActive(prevState.allowedQueues, code)
			}
			const completeMap = buildCompleteAllowedQueuesMap(queueGroups, prevState.allowedQueues, overrides)
			persistDelta({ allowedQueues: completeMap }, prevState)
			return _.merge({}, prevState, { allowedQueues: completeMap }) as ChatBotFlowExtraData
		})
	}

	const handleToggleGroup = (group: QueueGroup, isAllOn: boolean) => {
		setExtraData(prevState => {
			const overrides: Partial<Record<ChatBotQueueCode, boolean>> = {}
			group.queues.forEach(queue => {
				overrides[queue.code] = !isAllOn
			})
			const completeMap = buildCompleteAllowedQueuesMap(queueGroups, prevState.allowedQueues, overrides)
			persistDelta({ allowedQueues: completeMap }, prevState)
			return _.merge({}, prevState, { allowedQueues: completeMap }) as ChatBotFlowExtraData
		})
	}

	const isDelayActive = extraData.delay.active

	return (
		<ActionDialog
			title={"CONFIGURAÇÕES DO BOT"}
			openDialog={openDialog}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			cancelText="FECHAR"
			dialogContentClassName={classes.dialogContent}
		>
			<Grid container direction="column" spacing={2}>
				<Grid item>
					<Tabs
						color="primary"
						value={activeTab}
						onChange={(_, value) => setActiveTab(value)}
						className={classes.tabs}
						classes={{
							indicator: classes.tabIndicator
						}}
					>
						{configTabs.map((tab) => (
							<Tab
								key={tab.title}
								label={tab.title}
								value={tab.type}
								className={classes.tab}
							/>
						))}
					</Tabs>
				</Grid>

				<Grid item>
					<Loading loading={loading} customLoadingElement={<ChatBotConfigSkeleton />}>
						{activeTab === "general" && (
							<>
								<Box className={classes.configCard}>
									<Box className={classes.cardHeader}>
										<Box className={classes.cardHeaderText}>
											<Text.Body type="small1" fontWeight="bold" color="#2c2937">
												Delay entre as mensagens
											</Text.Body>
											<Text.Body type="smaller1" fontWeight="regular" color="#6a6578">
												Tempo de espera, em segundos, entre cada mensagem enviada pelo bot.
											</Text.Body>
										</Box>
										<Switch
											checked={isDelayActive}
											onChange={(_, checked) => handleLiveChange({ delay: { active: checked } })}
											classes={{
												root: classes.switchRoot,
												track: classes.switchTrack,
												thumb: classes.switchThumb,
												switchBase: classes.switchBase,
												checked: classes.switchBaseChecked
											}}
											edge="start"
										/>
									</Box>

									<Box className={classes.warningBanner}>
										<WarningIcon className={classes.warningIcon} />
										<Text.Body type="smaller1" fontWeight="regular" color="#7a5c00">
											Intervalos menores que 12 segundos podem aumentar significativamente a chance de bloqueio do seu número de WhatsApp.
										</Text.Body>
									</Box>

									{isDelayActive && (
										<Box className={classes.sliderRow}>
											<Text.Body type="smaller2" fontWeight="bold" color="#9a95a8" sx={{ flex: "none" }}>2s</Text.Body>
											<Slider
												min={MIN_DELAY}
												max={MAX_DELAY}
												step={1}
												value={sliderValue}
												onChange={(_, value) => setSliderValue(value as number)}
												onChangeCommitted={() => {
													handleLiveChange({ delay: { min_delay_in_seconds: sliderValue } })
												}}
												className={classes.slider}
											/>
											<Text.Body type="smaller2" fontWeight="bold" color="#9a95a8" sx={{ flex: "none" }}>40s</Text.Body>
											<Box className={classes.sliderValueBadge}>
												<Text.Body type="small2" fontWeight="bold" color="#5b3fd1">
													{sliderValue}s
												</Text.Body>
											</Box>
										</Box>
									)}
								</Box>

								<Box className={`${classes.configCard} ${classes.configCardSpacing}`}>
									<Box className={classes.cardHeader}>
										<Box className={classes.cardHeaderText}>
											<Text.Body type="small1" fontWeight="bold" color="#2c2937">
												Mostrar digitando
											</Text.Body>
											<Text.Body type="smaller1" fontWeight="regular" color="#6a6578">
												Simula a digitação antes de cada mensagem enviada pelo bot.
											</Text.Body>
										</Box>
										<Switch
											checked={!!extraData.shouldPresenceUpdateBeSentOnSendingMessages}
											onChange={(_, checked) => handleLiveChange({ shouldPresenceUpdateBeSentOnSendingMessages: checked })}
											classes={{
												root: classes.switchRoot,
												track: classes.switchTrack,
												thumb: classes.switchThumb,
												switchBase: classes.switchBase,
												checked: classes.switchBaseChecked
											}}
											edge="start"
										/>
									</Box>

									<ul className={classes.bulletsList}>
										<li>
											<Text.Body type="smaller1" fontWeight="regular" color="#5a5568">
												Caso essa opção esteja habilitada, mostraremos &quot;digitando&quot; ou &quot;gravando áudio&quot; para seu cliente antes de enviar mensagens.
											</Text.Body>
										</li>
										<li>
											<Text.Body type="smaller1" fontWeight="regular" color="#5a5568">
												Não será possível ver &quot;digitando&quot; e &quot;gravando áudio&quot; através da Letalk.
											</Text.Body>
										</li>
									</ul>

									<Box className={`${classes.warningBanner} ${classes.warningBannerDigitando}`}>
										<WarningIcon className={classes.warningIcon} />
										<Text.Body type="smaller1" fontWeight="regular" color="#7a5c00">
											Essa ação pode aumentar o tempo de envio de mensagens.
										</Text.Body>
									</Box>
								</Box>
							</>
						)}
						{activeTab === "queues" && (
							<QueuesSection
								allowedQueues={allowedQueues}
								groups={queueGroups}
								onToggleQueue={handleToggleQueue}
								onToggleGroup={handleToggleGroup}
							/>
						)}
					</Loading>
				</Grid>
			</Grid>
		</ActionDialog>
	)
}

export default ChatBotConfig
